import { useState } from "react";
import { motion } from "framer-motion";
import { CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

/* ───────────────────────────────────────── symbols ────────────────────────── */
const symbols = [
    { id: 0, value: 0 },
    { id: 1, value: 10 },
    { id: 2, value: 11 },
    { id: 3, value: 20 },
    { id: 4, value: 21 },
    { id: 5, value: 22 },
];

export default function TerminusCodeCracker({
                                                darkMode,
                                            }: {
    darkMode: boolean;
}) {
    /* ─────────────────────────── state ─────────────────────────── */
    const [x, setX] = useState<number | null>(null);
    const [y, setY] = useState<number | null>(null);
    const [z, setZ] = useState<number | null>(null);
    const [results, setResults] = useState<Array<number | null>>([
        null,
        null,
        null,
    ]);

    /* ─────────────────────────── helpers ────────────────────────── */
    const reset = () => {
        setX(null);
        setY(null);
        setZ(null);
        setResults([null, null, null]);
    };

    const handleSelect = (v: number) => {
        if (x === v) setX(null);
        else if (y === v) setY(null);
        else if (z === v) setZ(null);
        else if (x === null) setX(v);
        else if (y === null && v !== x) setY(v);
        else if (z === null && v !== x && v !== y) setZ(v);
    };

    const calculateResults = () => {
        if (x !== null && y !== null && z !== null) {
            setResults([2 * x + 11, 2 * z + y - 5, Math.abs(y + z - x)]);
        }
    };

    /* ─────────────────────────── styling ────────────────────────── */
    const baseBtn =
        "relative border-2 rounded-full w-16 h-16 m-1 flex items-center justify-center transition duration-300 ease-in-out transform hover:scale-110";

    const picked = {
        x: "bg-blue-400 border-blue-600",
        y: "bg-green-400 border-green-600",
        z: "bg-pink-400 border-pink-600",
    };

    const getColor = (v: number) => {
        const isSel = v === x || v === y || v === z;
        const key = v === x ? "x" : v === y ? "y" : "z";
        return isSel
            ? `${baseBtn} ${picked[key as keyof typeof picked]}`
            : `${baseBtn} ${
                darkMode
                    ? "bg-gray-800 border-gray-600 text-white hover:bg-gray-700"
                    : "bg-white border-gray-300 hover:bg-gray-100"
            }`;
    };

    /* ────────────────────────── renderer ────────────────────────── */
    const renderSymbol = (id: number) => {
        const val = symbols[id].value;
        let label = `Value: ${val}`;
        if (val === x) label += " (X)";
        else if (val === y) label += " (Y)";
        else if (val === z) label += " (Z)";

        return (
            <div key={id} className="relative group">
                {/* 💡 made the button itself `relative` (already done in baseBtn) so the badge anchors correctly */}
                <button onClick={() => handleSelect(val)} className={getColor(val)}>
                    {/* SVG ICONS – unchanged */}
                    {id === 0 && (
                        <svg viewBox="0 0 100 100" width="40" height="40">
                            <circle
                                cx="50"
                                cy="50"
                                r="20"
                                fill="white"
                                stroke="black"
                                strokeWidth="2"
                            />
                        </svg>
                    )}
                    {id === 1 && (
                        <svg viewBox="0 0 100 100" width="40" height="40">
                            <circle
                                cx="50"
                                cy="60"
                                r="20"
                                fill="white"
                                stroke="black"
                                strokeWidth="2"
                            />
                            <circle cx="50" cy="40" r="20" fill="black" />
                        </svg>
                    )}
                    {id === 2 && (
                        <svg viewBox="0 0 100 100" width="40" height="40">
                            <circle
                                cx="60"
                                cy="40"
                                r="20"
                                fill="white"
                                stroke="black"
                                strokeWidth="2"
                            />
                            <circle cx="40" cy="60" r="20" fill="black" />
                        </svg>
                    )}
                    {id === 3 && (
                        <svg viewBox="0 0 100 100" width="40" height="40">
                            <ellipse
                                cx="50"
                                cy="36"
                                rx="10"
                                ry="35"
                                fill="white"
                                stroke="black"
                                strokeWidth="2"
                            />
                            <ellipse
                                cx="50"
                                cy="70"
                                rx="10"
                                ry="29"
                                fill="white"
                                stroke="black"
                                strokeWidth="2"
                            />
                            <ellipse cx="30" cy="50" rx="30" ry="12" fill="black" />
                            <ellipse cx="70" cy="50" rx="30" ry="12" fill="black" />
                        </svg>
                    )}
                    {id === 4 && (
                        <svg viewBox="0 0 100 100" width="40" height="40">
                            <ellipse
                                cx="31"
                                cy="31"
                                rx="12"
                                ry="35"
                                fill="white"
                                stroke="black"
                                strokeWidth="2"
                                transform="rotate(-45 31 31)"
                            />
                            <ellipse
                                cx="69"
                                cy="69"
                                rx="12"
                                ry="35"
                                fill="white"
                                stroke="black"
                                strokeWidth="2"
                                transform="rotate(-45 69 69)"
                            />
                            <ellipse
                                cx="31"
                                cy="69"
                                rx="12"
                                ry="35"
                                fill="black"
                                transform="rotate(45 31 69)"
                            />
                            <ellipse
                                cx="69"
                                cy="31"
                                rx="12"
                                ry="35"
                                fill="black"
                                transform="rotate(45 69 31)"
                            />
                        </svg>
                    )}
                    {id === 5 && (
                        <svg viewBox="0 0 100 100" width="40" height="40">
                            <ellipse
                                cx="31"
                                cy="69"
                                rx="12"
                                ry="35"
                                fill="white"
                                stroke="black"
                                strokeWidth="2"
                                transform="rotate(45 31 69)"
                            />
                            <ellipse
                                cx="69"
                                cy="31"
                                rx="12"
                                ry="35"
                                fill="white"
                                stroke="black"
                                strokeWidth="2"
                                transform="rotate(45 69 31)"
                            />
                            <ellipse
                                cx="31"
                                cy="31"
                                rx="12"
                                ry="35"
                                fill="black"
                                transform="rotate(-45 31 31)"
                            />
                            <ellipse
                                cx="69"
                                cy="69"
                                rx="12"
                                ry="35"
                                fill="black"
                                transform="rotate(-45 69 69)"
                            />
                        </svg>
                    )}

                    {/* 💡 BADGE: now anchored to the button and nudged with translate */}
                    <span
                        className={
                            /* nudge 0.25 rem in from the edge */
                            "absolute bottom-[0px] right-[0px] text-xs bg-white text-black rounded-full px-1 shadow"
                        }
                    >
            {val}
          </span>
                </button>

                {/* Tooltip (unchanged) */}
                <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-black text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    {label}
                </div>
            </div>
        );
    };

    /* ─────────────────────────── UI ─────────────────────────── */
    return (
        <div
            className={`w-full h-full min-h-screen ${
                darkMode
                    ? "bg-gray-900 text-white"
                    : "bg-gradient-to-br from-indigo-300 via-purple-300 to-pink-200 text-gray-900"
            } flex justify-center items-center py-10 px-4`}
        >
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className={`w-full max-w-xl p-6 ${
                    darkMode ? "bg-gray-800 text-white" : "bg-white/80 shadow-xl"
                } rounded-2xl backdrop-blur`}
            >
                <CardHeader>
                    <CardTitle className="text-center text-3xl font-bold">
                        Terminus Code Cracker
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {/* symbol grid */}
                    <div className="mb-4">
                        <h2 className="text-lg font-semibold">Select Symbols</h2>
                        <div className="grid grid-cols-3 gap-2 justify-center items-center mt-2">
                            {symbols.map((_, idx) => renderSymbol(idx))}
                        </div>
                    </div>

                    {/* current selection */}
                    <div className="mb-4">
                        <h2 className="text-lg font-semibold">Current Selection</h2>
                        <div className="flex justify-center gap-4">
                            <div className="p-2">X: {x ?? "Not set"}</div>
                            <div className="p-2">Y: {y ?? "Not set"}</div>
                            <div className="p-2">Z: {z ?? "Not set"}</div>
                        </div>
                    </div>

                    {/* buttons */}
                    <div className="flex justify-center gap-4">
                        <Button onClick={calculateResults}>Crack Code</Button>
                        <Button variant="outline" onClick={reset}>
                            Reset
                        </Button>
                    </div>

                    {/* results */}
                    <div className="mt-4 space-y-1">
                        <p>Result&nbsp;1&nbsp;:&nbsp;{results[0] ?? "--"}</p>
                        <p>Result&nbsp;2&nbsp;:&nbsp;{results[1] ?? "--"}</p>
                        <p>Result&nbsp;3&nbsp;:&nbsp;{results[2] ?? "--"}</p>
                    </div>
                </CardContent>
            </motion.div>
        </div>
    );
}
